import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import translate from "../../../i18n/translate";
import { intl } from "../../../i18n/provider";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";

// Edits the EPRS login that Cellcom, Pelephone, Partner, Golan and 012 Mobile
// all depend on. Every card list and topup for those operators starts with
// this login, so when it changes they all stop together. The password is
// posted in the request body, never shown back, and a blank field on save
// leaves the stored value alone.
const EPRSCredentials = ({ isDarktheme }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [stored, setStored] = useState({ url: "", user: "", password_set: false });
  const [form, setForm] = useState({ url: "", user: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [balance, setBalance] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ApiRequest.post("eprs_credentials?operation=get");
      if (res?.data?.status === "success") {
        setStored({
          url: res.data.url || "",
          user: res.data.user || "",
          password_set: Boolean(res.data.password_set),
        });
        setForm((f) => ({ ...f, url: res.data.url || "", user: res.data.user || "" }));
      }
    } catch (err) {
      // the axios interceptor already reports the failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty =
    form.url.trim() !== stored.url ||
    form.user.trim() !== stored.user ||
    form.password.trim() !== "";

  const save = async (e) => {
    e.preventDefault();
    if (!dirty) return;

    const body = {};
    if (form.url.trim() !== stored.url) body.url = form.url.trim();
    if (form.user.trim() !== stored.user) body.user = form.user.trim();
    if (form.password.trim()) body.password = form.password.trim();

    setSaving(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("eprs_credentials?operation=update", body);
      if (res?.data?.status === "success") {
        setStored({
          url: res.data.url || "",
          user: res.data.user || "",
          password_set: Boolean(res.data.password_set),
        });
        setForm({ url: res.data.url || "", user: res.data.user || "", password: "" });
        setShowPassword(false);
        Toast.fire({ title: translate("eprsCredentialsSaved"), icon: "success" });
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("eprs_credentials?operation=test");
      setTestResult({ ok: res?.data?.status === "success", reason: res?.data?.reason });
    } catch (err) {
      setTestResult({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setTesting(false);
    }
  };

  const checkBalance = async () => {
    setChecking(true);
    setBalance(null);
    setTestResult(null);
    try {
      const res = await ApiRequest.post("eprs_credentials?operation=balance", {}, {
        timeout: 60000,
      });
      setBalance(
        res?.data?.status === "success"
          ? { ok: true, ...res.data }
          : { ok: false, reason: res?.data?.reason }
      );
    } catch (err) {
      setBalance({ ok: false, reason: "Something Went Wrong!" });
    } finally {
      setChecking(false);
    }
  };

  const field = (key, label, extra = {}) => (
    <TextField
      size="small"
      fullWidth
      value={form[key]}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      label={label}
      autoComplete="off"
      {...extra}
    />
  );

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("eprsCredentialsHint")}
      </Typography>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 2 }}>
          <Typography variant="subtitle1">{translate("Stored password")}</Typography>
          {loading ? (
            <CircularProgress size={18} />
          ) : stored.password_set ? (
            <Chip size="small" color="success" label={translate("set")} />
          ) : (
            <Chip size="small" color="warning" label={translate("not set")} />
          )}
        </Box>

        <Box component="form" onSubmit={save} sx={{ display: "grid", gap: 2 }}>
          {field("url", translate("EPRS URL"), { placeholder: "https://..." })}
          {field("user", translate("User name"))}
          {field("password", translate("Password"), {
            type: showPassword ? "text" : "password",
            placeholder: stored.password_set ? "••••••••  (" + intl.formatMessage({ id: "leave blank to keep" }) + ")" : "",
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label="toggle password visibility"
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          })}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="submit" variant="contained" disabled={saving || loading || !dirty}>
              {saving ? <CircularProgress size={20} color="inherit" /> : translate("Save")}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ padding: 3, maxWidth: 620, marginBottom: 3 }}>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          {translate("Test login")}
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("eprsTestHint")}
        </Typography>
        <Button
          variant="outlined"
          onClick={test}
          disabled={testing || loading || !stored.password_set || !stored.url || !stored.user}
        >
          {testing ? <CircularProgress size={20} color="inherit" /> : translate("Test login")}
        </Button>

        {testResult && (
          <Alert severity={testResult.ok ? "success" : "error"} sx={{ marginTop: 2 }}>
            {testResult.reason}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ padding: 3, maxWidth: 620 }}>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          {translate("Balance")}
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: 2, opacity: 0.75 }}>
          {translate("eprsBalanceHint")}
        </Typography>
        <Button
          variant="outlined"
          onClick={checkBalance}
          disabled={checking || loading || !stored.password_set || !stored.url || !stored.user}
        >
          {checking ? <CircularProgress size={20} color="inherit" /> : translate("Check balance")}
        </Button>

        {balance && !balance.ok && (
          <Alert severity="error" sx={{ marginTop: 2 }}>
            {balance.reason}
          </Alert>
        )}

        {balance && balance.ok && (
          // Left to spend is the figure that decides whether a topup goes
          // through, so it leads; the credit line it comes out of is the
          // detail underneath. Nothing left reads as a warning, not a success.
          <Alert severity={balance.available > 0 ? "success" : "warning"} sx={{ marginTop: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {balance.available_text}
            </Typography>
            <Typography variant="body2">{translate("Left to spend")}</Typography>
            <Box sx={{ marginTop: 1.5, display: "grid", gap: 0.25, opacity: 0.85 }}>
              <Typography variant="body2">
                {translate("Used")}: {balance.balance_text} &nbsp;·&nbsp;{" "}
                {translate("Credit limit")}: {balance.credit_text}
              </Typography>
              <Typography variant="caption">
                {translate("Branch")} {balance.branch_id} &nbsp;·&nbsp; {balance.checked_at}
              </Typography>
            </Box>
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps)(EPRSCredentials);
